import { clearAllUserCaches, clearApiCache } from '../lib/caches';
import { invalidate } from '../lib/query';
import { createStore, storage, useStore } from '../lib/store';
import {
  authenticate,
  authenticateWithToken,
  authorize,
  checkReachability as probeReachability,
  clearLibrariesCache,
  getLibraries,
  revokeSession,
  verifySession,
  type Authenticated,
  type RequestSession,
} from './abs';
import { AbsError, configureRequestDefaults, DEFAULT_REQUEST_TIMEOUT_MS, onDetachedTokenRefresh, onUnauthorized, refreshTokensFor } from './client';
import {
  applyDiscoveredLibraries,
  connectionFromSession,
  connectionIdFor,
  CONNECTION_TIMEOUT_CHOICES,
  DEFAULT_CONNECTION_OPTIONS,
  isUsableConnection,
  parseConnection,
  requiresReauth,
  sessionFromConnection,
  setAllLibrariesEnabledIn,
  setLibraryEnabledIn,
  toConnectionLibraries,
  type ConnectionOptions,
  type ConnectionsSnapshot,
  type SavedConnection,
  type SavedConnectionLibrary,
} from './connectionModel';
import { validateOnboardingInput, type ServerScheme } from './endpointValidator';
import { setProgressFromUser } from './progressStore';
import { adoptStoredTokens, CONNECTIONS_STORAGE_KEY, getSession, sessionStore, type AuthSession } from './session';
import type { Library, LoginResponse } from './types';
import { isMixedContent, normalizeServerUrl } from './url';

export * from './connectionModel';

// Several saved Audiobookshelf connections, each with its own tokens; one is
// active and mirrored into `sessionStore`, which the rest of the app reads.
// Port of Flutter's ConnectionProvider state machine
// (lib/providers/connection_provider.dart), with the fixes listed in
// src/data/README.md ("Server data" → Deviations).

export const CONNECTIONS_KEY = CONNECTIONS_STORAGE_KEY;

export interface ConnectionsState {
  connections: SavedConnection[];
  /** The connection the active session belongs to (null when signed out). */
  activeId: string | null;
  /** An atomic switch is verifying the target; the old server stays on screen. */
  switching: boolean;
  /** Last user-facing error from a connection operation. */
  error: string | null;
  /** Connection ids whose library list is being refreshed. */
  refreshingLibraries: Record<string, boolean>;
}

export class ConnectionError extends Error {
  constructor(
    message: string,
    readonly kind: 'validation' | 'auth' | 'network' | 'state' = 'state',
  ) {
    super(message);
    this.name = 'ConnectionError';
  }
}

export const MSG = {
  deactivated: 'This connection is deactivated.',
  reauth: 'This connection needs you to sign in again.',
  invalidSession: 'Your Audiobookshelf session is invalid. Please sign in again.',
  switchFailed: 'Unable to switch server right now. Please try again.',
  notFound: 'Connection not found.',
  credentialsRequired: 'Username and password are required.',
  tokenFlow: 'This connection uses an OAuth/token flow. Reconnect instead.',
  wrongCredentials: 'Incorrect username or password',
  apiKeyRejected: 'That API key was not accepted. Check it in Audiobookshelf (Settings → API Keys).',
  requestFailed: 'Unable to complete the request right now. Please try again.',
  credentialsUnverified: 'Credentials could not be verified.',
  credentialsFailed: 'Unable to validate credentials. Please try again.',
  connectFailed: 'Unable to connect. Please try again.',
  libraryDisabled: 'That library is disabled for this connection.',
  noResumable: 'No active saved connection is ready to resume.',
  mixedContent:
    'This app is served over HTTPS, so it cannot talk to an http:// server. Use your server’s HTTPS address or the built-in /abs proxy.',
  tooManyAttempts: 'Too many attempts. Wait a minute and try again.',
  differentAccount: 'That is a different account on this server. Add it as a new connection instead.',
} as const;

function loadSnapshot(): ConnectionsSnapshot {
  const raw = storage.read<Partial<ConnectionsSnapshot> | null>(CONNECTIONS_KEY, null);
  const connections = Array.isArray(raw?.connections) ? raw.connections.map(parseConnection).filter((c): c is SavedConnection => c !== null) : [];
  const activeId = typeof raw?.activeId === 'string' && connections.some((c) => c.id === raw.activeId) ? raw.activeId : null;
  return { version: 1, activeId, connections };
}

function initialState(): ConnectionsState {
  const snapshot = loadSnapshot();
  const session = getSession();
  let { connections, activeId } = snapshot;
  // First run with multi-connection support: adopt the signed-in session.
  if (session && !connections.some((c) => c.id === session.connectionId || (c.url === session.serverUrl && c.userId === session.userId))) {
    const migrated = connectionFromSession(session);
    connections = [...connections, migrated];
    activeId = migrated.id;
  }
  return { connections, activeId, switching: false, error: null, refreshingLibraries: {} };
}

export const connectionsStore = createStore<ConnectionsState>(initialState());

function persist(state: ConnectionsState): void {
  const snapshot: ConnectionsSnapshot = { version: 1, activeId: state.activeId, connections: state.connections };
  storage.write(CONNECTIONS_KEY, snapshot);
}

let lastPersisted: SavedConnection[] | null = null;
let lastPersistedActive: string | null | undefined;
connectionsStore.subscribe(() => {
  const s = connectionsStore.get();
  if (s.connections === lastPersisted && s.activeId === lastPersistedActive) return;
  lastPersisted = s.connections;
  lastPersistedActive = s.activeId;
  persist(s);
});
persist(connectionsStore.get());

const sameRecord = (a: SavedConnection, b: SavedConnection): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * The connections another tab saved (token rotation, edits, deletions),
 * merged into this tab's: per connection the newer record (`updatedAt`)
 * wins, so this tab never writes stale tokens back over rotated ones.
 * Connections missing there were deleted there, except this tab's active
 * one. The active connection stays per tab. Pure.
 */
export function mergeStoredConnections(ours: readonly SavedConnection[], theirs: readonly SavedConnection[], keepId: string | null): SavedConnection[] {
  const mine = new Map(ours.map((c) => [c.id, c]));
  const merged: SavedConnection[] = [];
  for (const stored of theirs) {
    const local = mine.get(stored.id);
    mine.delete(stored.id);
    merged.push(local && local.updatedAt > stored.updatedAt ? local : stored);
  }
  const kept = keepId ? mine.get(keepId) : undefined;
  if (kept) merged.push(kept);
  return merged;
}

function adoptStoredConnections(): void {
  const state = connectionsStore.get();
  const stored = loadSnapshot().connections;
  const keepId = getSession() ? state.activeId : null;
  const merged = mergeStoredConnections(state.connections, stored, keepId);
  const unchanged = merged.length === state.connections.length && merged.every((c, i) => sameRecord(c, state.connections[i] as SavedConnection));
  if (!unchanged) {
    // What is already stored needs no write back (no echo between tabs).
    if (merged.length === stored.length && merged.every((c, i) => c === stored[i])) {
      lastPersisted = merged;
      lastPersistedActive = state.activeId;
    }
    connectionsStore.set((s) => ({ ...s, connections: merged }));
  }
  // This tab's session takes the active connection's rotated tokens.
  adoptStoredTokens();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === CONNECTIONS_KEY) adoptStoredConnections();
  });
}

// ---------------------------------------------------------------- reads

export function listConnections(): SavedConnection[] {
  return connectionsStore.get().connections;
}

export function getConnection(id: string): SavedConnection | undefined {
  return connectionsStore.get().connections.find((c) => c.id === id);
}

export function getActiveConnection(): SavedConnection | null {
  const { activeId, connections } = connectionsStore.get();
  return connections.find((c) => c.id === activeId) ?? null;
}

export function useConnections(): ConnectionsState {
  return useStore(connectionsStore);
}

const selectActive = (s: ConnectionsState): SavedConnection | null => s.connections.find((c) => c.id === s.activeId) ?? null;

export function useActiveConnection(): SavedConnection | null {
  return useStore(connectionsStore, selectActive);
}

export type AppSessionState =
  | 'authenticated'
  | 'switchingServer'
  | 'noActiveConnectionConfigured'
  | 'firstRunNoServerConfigured'
  | 'unauthenticatedOnActiveServer';

/** Which gate the shell shows (Flutter `appSessionState`). */
export function appSessionState(state: ConnectionsState = connectionsStore.get(), session: AuthSession | null = getSession()): AppSessionState {
  if (session) return 'authenticated';
  if (state.switching) return 'switchingServer';
  if (state.connections.length > 0 && !state.connections.some((c) => c.isActive)) return 'noActiveConnectionConfigured';
  if (state.connections.length === 0) return 'firstRunNoServerConfigured';
  return 'unauthenticatedOnActiveServer';
}

export function useAppSessionState(): AppSessionState {
  const state = useStore(connectionsStore);
  const session = useStore(sessionStore);
  return appSessionState(state, session);
}

// ---------------------------------------------------------------- mutation helpers

function patchState(patch: Partial<ConnectionsState>): void {
  connectionsStore.set((s) => ({ ...s, ...patch }));
}

function setError(error: string | null): void {
  if (connectionsStore.get().error !== error) patchState({ error });
}

function updateConnectionRecord(id: string, update: (c: SavedConnection) => SavedConnection): SavedConnection | undefined {
  let result: SavedConnection | undefined;
  connectionsStore.set((s) => {
    const index = s.connections.findIndex((c) => c.id === id);
    if (index < 0) return s;
    const prev = s.connections[index] as SavedConnection;
    const next = update(prev);
    result = next;
    if (next === prev) return s;
    const connections = [...s.connections];
    connections[index] = { ...next, updatedAt: Date.now() };
    result = connections[index];
    return { ...s, connections };
  });
  return result;
}

function upsertConnection(next: SavedConnection): SavedConnection {
  connectionsStore.set((s) => {
    const index = s.connections.findIndex((c) => c.id === next.id);
    const connections = [...s.connections];
    if (index < 0) connections.push(next);
    else connections[index] = next;
    return { ...s, connections };
  });
  return next;
}

/** RFC 9110 token characters. */
const HEADER_NAME = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;
/** Latin-1 without NUL, CR or LF: anything else makes fetch() throw. */
function isValidHeaderValue(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code > 0xff || code === 0 || code === 0x0a || code === 0x0d) return false;
  }
  return true;
}

/**
 * The custom headers sent with every request. Rows a backup import or an
 * older build saved with an invalid name or value are left out, as one would
 * make every fetch throw ("Could not reach the server").
 */
function headersOf(options: Pick<ConnectionOptions, 'customHeaders'> | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const h of options?.customHeaders ?? []) {
    const name = h.name.trim();
    if (HEADER_NAME.test(name) && isValidHeaderValue(h.value)) headers[name] = h.value;
  }
  return headers;
}

/** A request session for a (possibly inactive) connection, with its custom headers. */
function requestSessionOf(c: Pick<SavedConnection, 'url' | 'accessToken' | 'refreshToken' | 'options'>): RequestSession {
  return { serverUrl: c.url, accessToken: c.accessToken, refreshToken: c.refreshToken, headers: headersOf(c.options) };
}

/**
 * The connection timeout only raises the request timeout above the 20 s
 * default: Flutter never applied its 10 s value (its requests wait 30 s), and
 * a shorter cap made ordinary requests on slow links fail.
 */
export function requestTimeoutMs(options: Pick<ConnectionOptions, 'timeoutSeconds'>): number {
  return Math.max(options.timeoutSeconds * 1000, DEFAULT_REQUEST_TIMEOUT_MS);
}

/** Seconds a request really waits for a saved `timeoutSeconds`. */
export function effectiveTimeoutSeconds(timeoutSeconds: number): number {
  return requestTimeoutMs({ timeoutSeconds: Number.isFinite(timeoutSeconds) && timeoutSeconds > 0 ? timeoutSeconds : DEFAULT_CONNECTION_OPTIONS.timeoutSeconds }) / 1000;
}

export interface ConnectionTimeoutChoice {
  /** `timeoutSeconds` saved for this choice (one of Flutter's values, so backups stay compatible). */
  value: number;
  /** Seconds a request really waits with it. */
  effectiveSeconds: number;
  /** The choice holding the default timeout. */
  isDefault: boolean;
}

/**
 * The timeout menu: Flutter's 5 / 10 / 15 / 30 s grouped by what requests
 * really wait (never below the 20 s default), so no two choices
 * behave the same. A group saves the default (10) when it holds it, else its
 * first Flutter value. Today: 10 (waits 20 s, the default) and 30.
 */
export function connectionTimeoutChoices(): ConnectionTimeoutChoice[] {
  const defaultSeconds = DEFAULT_CONNECTION_OPTIONS.timeoutSeconds;
  const defaultEffective = effectiveTimeoutSeconds(defaultSeconds);
  const choices: ConnectionTimeoutChoice[] = [];
  for (const seconds of CONNECTION_TIMEOUT_CHOICES) {
    const effectiveSeconds = effectiveTimeoutSeconds(seconds);
    const existing = choices.find((c) => c.effectiveSeconds === effectiveSeconds);
    if (existing) {
      if (seconds === defaultSeconds) existing.value = seconds;
      continue;
    }
    choices.push({ value: seconds, effectiveSeconds, isDefault: effectiveSeconds === defaultEffective });
  }
  return choices;
}

function applyRequestDefaults(c: SavedConnection | null): void {
  const options = c?.options ?? DEFAULT_CONNECTION_OPTIONS;
  configureRequestDefaults({ timeoutMs: requestTimeoutMs(options), headers: headersOf(options) });
}

function markReauthRequired(id: string, reason: string): void {
  updateConnectionRecord(id, (c) => ({
    ...c,
    accessToken: '',
    refreshToken: undefined,
    authState: 'reauthRequired',
    authFailureReason: reason,
    authFailureAt: Date.now(),
  }));
}

// ---------------------------------------------------------------- session mirroring

/** Keeps the active connection in step with `sessionStore` (token rotation, legacy logins). */
function syncFromSession(): void {
  const session = getSession();
  const state = connectionsStore.get();
  if (!session) {
    if (state.activeId !== null && !state.switching) patchState({ activeId: null });
    return;
  }
  const existing =
    state.connections.find((c) => c.id === session.connectionId) ??
    state.connections.find((c) => c.url === session.serverUrl && c.userId === session.userId);
  if (!existing) {
    const created = connectionFromSession(session);
    upsertConnection(created);
    patchState({ activeId: created.id, error: null });
    applyRequestDefaults(created);
    if (session.connectionId !== created.id) sessionStore.set({ ...session, connectionId: created.id });
    return;
  }
  const tokensChanged =
    existing.accessToken !== session.accessToken ||
    existing.refreshToken !== session.refreshToken ||
    existing.authState !== 'ready' ||
    (session.serverVersion && existing.serverVersion !== session.serverVersion) ||
    (session.defaultLibraryId && existing.defaultLibraryId !== session.defaultLibraryId);
  if (tokensChanged) {
    updateConnectionRecord(existing.id, (c) => ({
      ...c,
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      authState: 'ready',
      authFailureReason: undefined,
      authFailureAt: undefined,
      serverVersion: session.serverVersion ?? c.serverVersion,
      defaultLibraryId: session.defaultLibraryId ?? c.defaultLibraryId,
      username: session.username || c.username,
      lastUsedAt: Date.now(),
    }));
  }
  if (state.activeId !== existing.id) {
    patchState({ activeId: existing.id });
    applyRequestDefaults(existing);
  }
  if (session.connectionId !== existing.id) sessionStore.set({ ...session, connectionId: existing.id });
}

sessionStore.subscribe(syncFromSession);
syncFromSession();
applyRequestDefaults(getActiveConnection());

function connectionOfAccount(account: { serverUrl: string; userId: string; connectionId?: string }): SavedConnection | undefined {
  const { connections } = connectionsStore.get();
  return (
    (account.connectionId ? connections.find((c) => c.id === account.connectionId) : undefined) ??
    connections.find((c) => c.url === account.serverUrl && c.userId === account.userId)
  );
}

// A session the server rejected (and could not refresh) needs a new sign-in.
onUnauthorized((session) => {
  const rejected = connectionOfAccount(session);
  if (rejected) markReauthRequired(rejected.id, MSG.invalidSession);
});

// A refresh that finished after a switch already consumed the old refresh token: keep the new pair.
onDetachedTokenRefresh((tokens) => {
  const c = connectionOfAccount(tokens);
  if (c && c.authState === 'ready') updateConnectionRecord(c.id, (prev) => ({ ...prev, accessToken: tokens.accessToken, refreshToken: tokens.refreshToken ?? prev.refreshToken }));
});

// ---------------------------------------------------------------- switching

export interface ConnectionSwitchHooks {
  /** Runs before the active session changes (default: closes the player so no stream outlives its server). */
  beforeSwitch: () => void | Promise<void>;
}

const defaultHooks: ConnectionSwitchHooks = {
  async beforeSwitch() {
    const { player } = await import('../player/engine');
    if (player.state.itemId) await player.close();
  },
};

let hooks: ConnectionSwitchHooks = defaultHooks;

/** Overrides the switch hooks (tests, or a shell that tears down more state). */
export function setConnectionSwitchHooks(next: Partial<ConnectionSwitchHooks>): void {
  hooks = { ...defaultHooks, ...next };
}

/**
 * Drops the old account's server data right before its session is swapped
 * (synchronously, with no await in between): query entries go idle so
 * mounted screens neither refetch from the old server nor replay old
 * requests against the new one; the swap itself aborts the old account's
 * requests in flight (client.ts).
 */
function dropServerCaches(): void {
  invalidate('', { refetch: false });
  clearLibrariesCache();
}

/** Makes `connection` the active one: resets server caches and swaps the session. */
async function commitActive(connection: SavedConnection, auth: LoginResponse | null): Promise<void> {
  const identityChanges = getSession()?.connectionId !== connection.id;
  if (identityChanges) await hooks.beforeSwitch();
  const committed =
    updateConnectionRecord(connection.id, () => ({ ...connection, authState: 'ready', authFailureReason: undefined, authFailureAt: undefined, lastUsedAt: Date.now() })) ??
    upsertConnection(connection);
  if (identityChanges) dropServerCaches();
  patchState({ activeId: committed.id, error: null });
  applyRequestDefaults(committed);
  sessionStore.set(
    sessionFromConnection(committed, {
      defaultLibraryId: auth?.userDefaultLibraryId ?? committed.defaultLibraryId,
      serverVersion: auth?.serverSettings?.version ?? committed.serverVersion,
      username: auth?.user?.username ?? committed.username ?? '',
    }),
  );
  if (auth?.user) setProgressFromUser(auth.user);
  // Cached responses are keyed by user in the service worker; this only frees the old account's.
  if (identityChanges) await clearApiCache().catch(() => undefined);
}

type VerifyOutcome = { ok: true; connection: SavedConnection; auth: LoginResponse } | { ok: false; reason: 'auth' | 'transient' };

/** Verifies a saved connection's token, refreshing it once if it expired. */
async function verifyConnection(c: SavedConnection): Promise<VerifyOutcome> {
  const session = requestSessionOf(c);
  try {
    return { ok: true, connection: c, auth: await verifySession(session) };
  } catch (err) {
    if (!(err instanceof AbsError) || (err.status !== 401 && err.status !== 403)) return { ok: false, reason: 'transient' };
  }
  const refreshed = await refreshTokensFor(session, session.headers);
  if (refreshed.status !== 'ok') return { ok: false, reason: refreshed.status === 'failed' ? 'transient' : 'auth' };
  const { accessToken, refreshToken } = refreshed;
  // The server consumed the old refresh token: store the new pair now, whatever happens next.
  updateConnectionRecord(c.id, (prev) => ({ ...prev, accessToken, refreshToken }));
  const next = { ...c, accessToken, refreshToken };
  try {
    return { ok: true, connection: next, auth: await verifySession(requestSessionOf(next)) };
  } catch (err) {
    return { ok: false, reason: err instanceof AbsError && (err.status === 401 || err.status === 403) ? 'auth' : 'transient' };
  }
}

/**
 * Atomically switches to a saved connection: verifies its token while the
 * current server stays on screen (`switching`), then swaps the session,
 * resets server caches and closes the player. Resolves false when the switch
 * is not possible, with `connectionsStore.error` set — except while another
 * switch is running, which resolves false silently (Flutter) and leaves
 * `switching` true.
 */
export async function switchConnection(id: string): Promise<boolean> {
  const c = getConnection(id);
  if (!c) {
    setError(MSG.notFound);
    return false;
  }
  if (!c.isActive) {
    setError(MSG.deactivated);
    return false;
  }
  if (requiresReauth(c)) {
    setError(MSG.reauth);
    return false;
  }
  if (getSession()?.connectionId === c.id) return true;
  if (connectionsStore.get().switching) return false;
  patchState({ switching: true, error: null });
  try {
    const outcome = await verifyConnection(c);
    if (!outcome.ok) {
      if (outcome.reason === 'auth') {
        markReauthRequired(c.id, MSG.invalidSession);
        setError(MSG.invalidSession);
      } else {
        setError(MSG.switchFailed);
      }
      return false;
    }
    await commitActive(outcome.connection, outcome.auth);
    return true;
  } catch {
    setError(MSG.switchFailed);
    return false;
  } finally {
    patchState({ switching: false });
  }
}

/** "Return to Library": switch to the first active connection that doesn't need a new sign-in. */
export async function returnToLibrary(): Promise<boolean> {
  const target = listConnections().find(isUsableConnection);
  if (!target) {
    setError(MSG.noResumable);
    return false;
  }
  return switchConnection(target.id);
}

/**
 * Startup check of the active session (Flutter `loadConnectionDetails`):
 * refreshes progress and server version, and moves away from a deactivated
 * connection. Auth failures go through the normal 401 → sign-in flow.
 */
export async function verifyActiveConnection(): Promise<void> {
  const session = getSession();
  const active = getActiveConnection();
  if (!session) return;
  if (active && !active.isActive) {
    await fallbackFrom(active.id);
    return;
  }
  try {
    const auth = await authorize();
    setProgressFromUser(auth.user);
    const current = getSession();
    if (current && current.connectionId === session.connectionId) {
      const serverVersion = auth.serverSettings?.version ?? current.serverVersion;
      const defaultLibraryId = auth.userDefaultLibraryId ?? current.defaultLibraryId;
      if (serverVersion !== current.serverVersion || defaultLibraryId !== current.defaultLibraryId) {
        sessionStore.set({ ...current, serverVersion, defaultLibraryId });
      }
    }
  } catch {
    // Offline or transient: keep the session (cached data and downloads still work).
  }
}

/** After the active connection was deactivated or deleted: switch to another usable one, else sign out locally. */
async function fallbackFrom(id: string): Promise<void> {
  const next = listConnections().find((c) => c.id !== id && isUsableConnection(c));
  if (next && (await switchConnection(next.id))) return;
  await signOutLocally();
}

async function signOutLocally(): Promise<void> {
  const hadSession = !!getSession();
  if (hadSession) {
    await hooks.beforeSwitch();
    dropServerCaches();
  }
  sessionStore.set(null);
  patchState({ activeId: null });
  applyRequestDefaults(null);
  if (hadSession) await clearApiCache().catch(() => undefined);
}

/**
 * Signs out of the active connection. By default the connection keeps its
 * tokens so "Return to Library" works (Flutter `logout`); `forget: true` also
 * revokes the refresh token, requires a new sign-in and drops cached covers.
 */
export async function signOut(opts: { forget?: boolean } = {}): Promise<void> {
  const active = getActiveConnection();
  const session = getSession();
  if (opts.forget && session) await revokeSession(session);
  if (opts.forget && active) markReauthRequired(active.id, 'Signed out.');
  await signOutLocally();
  if (opts.forget) await clearAllUserCaches().catch(() => undefined);
}

// ---------------------------------------------------------------- add / edit / delete

export type ConnectionCredentials = { type: 'password'; username: string; password: string } | { type: 'apiKey'; token: string };

export interface AddConnectionInput {
  /** Scheme picker value; ignored when `url` already has a scheme or is a same-origin path like `/abs`. */
  scheme?: ServerScheme;
  url: string;
  credentials: ConnectionCredentials;
  nickname?: string;
  options?: Partial<ConnectionOptions>;
  /** Make the new connection active right away (Flutter behaviour). Default true. */
  activate?: boolean;
}

/** Validates and normalises the server address. Same-origin paths (`/abs`) resolve against this page. */
export function resolveServerAddress(scheme: ServerScheme | undefined, input: string): string {
  const raw = input.trim();
  if (raw.startsWith('/')) {
    try {
      return normalizeServerUrl(raw);
    } catch (err) {
      throw new ConnectionError((err as Error).message, 'validation');
    }
  }
  const result = validateOnboardingInput(scheme ?? 'https://', raw);
  if (!result.ok) throw new ConnectionError(result.error, 'validation');
  return result.url;
}

/**
 * Flutter's sign-in error copy (`ConnectionProvider.connect`, spec 10 §12.3),
 * plus the PWA's rate-limit and API-key messages. A refused API key (the
 * PWA's token sign-in) never reads as a wrong username or password. Server
 * failures get Flutter's fallback sentence, never the server's raw response
 * body; network errors keep their own sentence ("Could not reach…", "…too long…").
 */
export function loginErrorMessage(err: unknown, opts: { apiKey?: boolean } = {}): string {
  if (err instanceof ConnectionError) return err.message;
  const rejected = opts.apiKey ? MSG.apiKeyRejected : MSG.wrongCredentials;
  if (err instanceof AbsError) {
    if (err.status === 401 || (opts.apiKey && err.status === 403)) return rejected;
    if (err.status === 429) return MSG.tooManyAttempts;
    if (err.isNetworkError && err.message) return err.message;
    return MSG.requestFailed;
  }
  if (err instanceof Error && /401/.test(err.message)) return rejected;
  return MSG.connectFailed;
}

async function authenticateWith(url: string, creds: ConnectionCredentials, headers: Record<string, string> = {}): Promise<Authenticated> {
  if (creds.type === 'password') {
    const username = creds.username.trim();
    if (!username || !creds.password) throw new ConnectionError(MSG.credentialsRequired, 'validation');
    return authenticate(url, username, creds.password, headers);
  }
  const token = creds.token.trim();
  if (!token) throw new ConnectionError('Enter an API key.', 'validation');
  return authenticateWithToken(url, token, headers);
}

async function fetchLibrariesFor(session: RequestSession): Promise<SavedConnectionLibrary[] | null> {
  try {
    return toConnectionLibraries(await getLibraries(session));
  } catch {
    return null;
  }
}

/**
 * Adds (or re-authenticates) a connection: validates the address, signs in
 * without touching the active session, discovers libraries, then (by
 * default) makes it the active connection. Throws `ConnectionError` with
 * user-facing copy.
 */
export async function addConnection(input: AddConnectionInput): Promise<SavedConnection> {
  const url = resolveServerAddress(input.scheme, input.url);
  if (isMixedContent(url)) throw new ConnectionError(MSG.mixedContent, 'validation');
  const headers = headersOf({ customHeaders: input.options?.customHeaders ?? [] });
  let auth: Authenticated;
  try {
    auth = await authenticateWith(url, input.credentials, headers);
  } catch (err) {
    const kind = err instanceof AbsError && err.isNetworkError ? 'network' : 'auth';
    throw err instanceof ConnectionError ? err : new ConnectionError(loginErrorMessage(err, { apiKey: input.credentials.type === 'apiKey' }), kind);
  }
  const { session, user } = auth;
  const id = connectionIdFor(session.userId, url);
  const existing = getConnection(id);
  const base = existing ?? connectionFromSession({ ...session, connectionId: id });
  const libraries = await fetchLibrariesFor({ ...session, headers });
  let next: SavedConnection = {
    ...base,
    url,
    userId: session.userId,
    username: session.username,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    authType: session.authType ?? (input.credentials.type === 'password' ? 'password' : 'apiKey'),
    authState: 'ready',
    authFailureReason: undefined,
    authFailureAt: undefined,
    serverVersion: session.serverVersion ?? base.serverVersion,
    defaultLibraryId: session.defaultLibraryId ?? base.defaultLibraryId,
    nickname: input.nickname?.trim() || base.nickname,
    isActive: true,
    options: { ...base.options, ...input.options, customHeaders: input.options?.customHeaders ?? base.options.customHeaders },
    lastUsedAt: Date.now(),
    updatedAt: Date.now(),
  };
  if (libraries) next = applyDiscoveredLibraries(next, libraries);
  upsertConnection(next);
  if (input.activate !== false) {
    await commitActive(next, { user, userDefaultLibraryId: session.defaultLibraryId, serverSettings: { version: session.serverVersion } });
  }
  return getConnection(id) ?? next;
}

export function updateConnectionNickname(id: string, nickname: string | null | undefined): void {
  updateConnectionRecord(id, (c) => ({ ...c, nickname: nickname?.trim() || undefined }));
}

export function updateConnectionOptions(id: string, options: Partial<ConnectionOptions>): void {
  const next = updateConnectionRecord(id, (c) => ({ ...c, options: { ...c.options, ...options, customHeaders: options.customHeaders ?? c.options.customHeaders } }));
  if (next && connectionsStore.get().activeId === id) applyRequestDefaults(next);
}

/**
 * Activates or deactivates a connection. Deactivating the current one moves
 * to another usable connection, or signs out (the gate then offers the rest).
 */
export async function setConnectionActive(id: string, isActive: boolean): Promise<void> {
  const c = getConnection(id);
  if (!c || c.isActive === isActive) return;
  updateConnectionRecord(id, (prev) => ({ ...prev, isActive }));
  if (!isActive && connectionsStore.get().activeId === id && getSession()) await fallbackFrom(id);
}

/** Deletes a connection (revoking its refresh token). Downloads made through it stay on the device. */
export async function deleteConnection(id: string): Promise<void> {
  const c = getConnection(id);
  if (!c) return;
  const wasActive = connectionsStore.get().activeId === id && !!getSession();
  connectionsStore.set((s) => ({ ...s, connections: s.connections.filter((x) => x.id !== id) }));
  if (c.refreshToken) void revokeSession(requestSessionOf(c));
  if (wasActive) await fallbackFrom(id);
}

function credentialErrorMessage(err: unknown, creds: ConnectionCredentials): string {
  if (err instanceof ConnectionError) return err.message;
  if (err instanceof AbsError) {
    if (err.status === 401 || (creds.type === 'apiKey' && err.status === 403)) return creds.type === 'apiKey' ? MSG.apiKeyRejected : `${MSG.wrongCredentials}.`;
    if (err.status === 429) return MSG.tooManyAttempts;
    // A server failure's raw response body is never shown.
    if (!err.isNetworkError) return MSG.credentialsFailed;
    return err.message || MSG.credentialsFailed;
  }
  return MSG.credentialsFailed;
}

async function validateCredentials(id: string, creds: ConnectionCredentials): Promise<{ connection: SavedConnection; auth: Authenticated }> {
  if (creds.type === 'password' && (!creds.username.trim() || !creds.password)) throw new ConnectionError(MSG.credentialsRequired, 'validation');
  const connection = getConnection(id);
  if (!connection) throw new ConnectionError(MSG.notFound, 'state');
  if (connection.authType !== creds.type) throw new ConnectionError(MSG.tokenFlow, 'state');
  try {
    const headers = headersOf(connection.options);
    const auth = await authenticateWith(connection.url, creds, headers);
    await verifySession({ ...auth.session, headers }).catch(() => {
      throw new ConnectionError(MSG.credentialsUnverified, 'auth');
    });
    return { connection, auth };
  } catch (err) {
    throw new ConnectionError(credentialErrorMessage(err, creds), err instanceof AbsError && err.isNetworkError ? 'network' : 'auth');
  }
}

/** Checks credentials against the server without saving anything (the test session is revoked again). */
export async function testCredentials(id: string, creds: ConnectionCredentials): Promise<void> {
  const { connection, auth } = await validateCredentials(id, creds);
  if (creds.type === 'password') void revokeSession({ ...auth.session, headers: headersOf(connection.options) });
}

/** Replaces a connection's credentials; updates the live session when it is the active one. */
export async function updateCredentials(id: string, creds: ConnectionCredentials): Promise<void> {
  const { connection, auth } = await validateCredentials(id, creds);
  // A connection is one account (its id is `abs:<userId>@url`): another
  // user's credentials would swap the live session without a switch.
  if (connection.userId && auth.session.userId !== connection.userId) throw new ConnectionError(MSG.differentAccount, 'auth');
  const next = updateConnectionRecord(id, (c) => ({
    ...c,
    userId: auth.session.userId,
    username: auth.session.username,
    accessToken: auth.session.accessToken,
    refreshToken: auth.session.refreshToken,
    authState: 'ready',
    authFailureReason: undefined,
    authFailureAt: undefined,
    lastUsedAt: Date.now(),
  }));
  if (!next) return;
  const session = getSession();
  if (session && session.connectionId === connection.id) {
    sessionStore.set({ ...session, accessToken: next.accessToken, refreshToken: next.refreshToken, username: next.username ?? session.username, userId: next.userId });
    setProgressFromUser(auth.user);
  }
}

// ---------------------------------------------------------------- libraries


/**
 * Re-discovers a connection's libraries. A failed request leaves the saved
 * list untouched (Flutter wiped it) and rethrows.
 */
export async function refreshConnectionLibraries(id: string): Promise<SavedConnectionLibrary[]> {
  const c = getConnection(id);
  if (!c) throw new ConnectionError(MSG.notFound);
  patchState({ refreshingLibraries: { ...connectionsStore.get().refreshingLibraries, [id]: true } });
  try {
    const isCurrent = getSession()?.connectionId === id;
    let session = requestSessionOf(c);
    let libraries: Library[];
    try {
      // The active connection goes through the normal client (token refresh, 401 handling).
      libraries = await getLibraries(isCurrent ? undefined : session);
    } catch (err) {
      if (isCurrent || !(err instanceof AbsError && err.status === 401 && session.refreshToken)) throw err;
      const refreshed = await refreshTokensFor(session, session.headers);
      if (refreshed.status !== 'ok') throw err;
      const { accessToken, refreshToken } = refreshed;
      updateConnectionRecord(id, (prev) => ({ ...prev, accessToken, refreshToken }));
      session = { ...session, accessToken, refreshToken };
      libraries = await getLibraries(session);
    }
    const discovered = toConnectionLibraries(libraries);
    saveConnectionLibraries(id, discovered);
    return discovered;
  } finally {
    const { [id]: _done, ...rest } = connectionsStore.get().refreshingLibraries;
    void _done;
    patchState({ refreshingLibraries: rest });
  }
}

/** Stores discovered libraries with Flutter's enable/selection rules. */
export function saveConnectionLibraries(id: string, libraries: SavedConnectionLibrary[], enabled?: readonly string[]): void {
  updateConnectionRecord(id, (c) => {
    const next = applyDiscoveredLibraries(c, libraries, enabled);
    const same =
      JSON.stringify(next.libraries) === JSON.stringify(c.libraries) &&
      next.enabledLibraryIds.join('|') === c.enabledLibraryIds.join('|') &&
      next.selectedLibraryId === c.selectedLibraryId;
    return same ? c : next;
  });
}

export function setLibraryEnabled(id: string, libraryId: string, enabled: boolean): void {
  updateConnectionRecord(id, (c) => setLibraryEnabledIn(c, libraryId, enabled));
}

export function setAllLibrariesEnabled(id: string, enabled: boolean): void {
  updateConnectionRecord(id, (c) => setAllLibrariesEnabledIn(c, enabled));
}

/** Selects a connection's library (Flutter `setLibraryId`); rejects libraries that are disabled. */
export function selectConnectionLibrary(id: string, libraryId: string): boolean {
  const c = getConnection(id);
  if (!c) return false;
  if (c.enabledLibraryIds.length > 0 && !c.enabledLibraryIds.includes(libraryId)) {
    setError(MSG.libraryDisabled);
    return false;
  }
  updateConnectionRecord(id, (prev) =>
    prev.selectedLibraryId === libraryId && (prev.enabledLibraryIds.length === 0 || prev.enabledLibraryIds.includes(libraryId))
      ? prev
      : { ...prev, selectedLibraryId: libraryId, enabledLibraryIds: [...new Set([...prev.enabledLibraryIds, libraryId])].sort() },
  );
  setError(null);
  return true;
}

/** Reachability probe for the onboarding URL field. */
export function checkReachability(scheme: ServerScheme | undefined, input: string, signal?: AbortSignal): Promise<boolean> {
  let url: string;
  try {
    url = resolveServerAddress(scheme, input);
  } catch {
    return Promise.resolve(false);
  }
  return probeReachability(url, signal);
}

